import { Header, notify } from "@general-backoffice/core";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardBody, Col, Container, Row } from "reactstrap";

import { messagingConsentsEndpoints } from "../../../../api";
import ConsentsForm from "../../../../components/shared/ConsentsForm";

// Local types for notification settings
type Consents = Record<string, boolean | undefined>;

const NotificationSettings: React.FC = () => {
  const { t } = useTranslation("dashboard");

  // State
  const [consents, setConsents] = useState<Consents>({});
  const [initialConsents, setInitialConsents] = useState<Consents>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConsents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await messagingConsentsEndpoints.getMyConsents();
      const consentsData = data || {};
      setConsents(consentsData);
      setInitialConsents(consentsData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined;
      setError(message || t("communicationPreferences.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Load consents on mount
  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  // Handle consent change
  const handleConsentChange = (key: string, value: boolean) => {
    setConsents((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Check if there are unsaved changes
  const hasChanges = JSON.stringify(consents) !== JSON.stringify(initialConsents);

  // Save changes
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Only send changed values
      const changedConsents: Consents = {};
      Object.keys(consents).forEach((key) => {
        if (consents[key] !== initialConsents[key]) {
          changedConsents[key] = consents[key];
        }
      });

      if (Object.keys(changedConsents).length > 0) {
        await messagingConsentsEndpoints.updateMyConsents(changedConsents);
      }

      setInitialConsents(consents);
      notify.success(t("communicationPreferences.saveSuccess"));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined;
      setError(message || t("communicationPreferences.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to initial values
  const handleReset = () => {
    setConsents(initialConsents);
    setError(null);
  };

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">{t("communicationPreferences.title")}</h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-4">
                    {error}
                    <button type="button" className="close" onClick={() => setError(null)}>
                      <span>&times;</span>
                    </button>
                  </Alert>
                )}

                {isLoading ? (
                  <div className="text-center py-4">
                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">{t("communicationPreferences.loading")}</p>
                  </div>
                ) : (
                  <>
                    <ConsentsForm
                      consents={consents}
                      onChange={handleConsentChange}
                      disabled={isSaving}
                      loading={isSaving}
                    />

                    <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top">
                      {hasChanges && (
                        <Button color="secondary" onClick={handleReset} disabled={isSaving} className="mr-2">
                          {t("communicationPreferences.reset")}
                        </Button>
                      )}
                      <Button color="primary" onClick={handleSave} disabled={isSaving || !hasChanges}>
                        {isSaving ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            {t("communicationPreferences.saving")}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save mr-2"></i>
                            {t("communicationPreferences.saveChanges")}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default NotificationSettings;
